import { z } from 'zod';
import {
  LabTestCollectionMethodT as LabTestCollectionMethodTValue,
  type LabMarkerTVitalLabMarkerMetadataT as LabMarkerT,
  type LabTestCollectionMethodT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { LabOrderDraft } from './types.js';
import { validateSameLabMarkers } from './validators/sameLabMarkers.js';

const labTestCollectionMethodValues = Object.values(
  LabTestCollectionMethodTValue
) as LabTestCollectionMethodT[];
const labTestCollectionMethodSchema = z.enum([...labTestCollectionMethodValues] as [
  LabTestCollectionMethodT,
  ...LabTestCollectionMethodT[],
]);

const icd10CodeSchema = z.object({
  code: z.string(),
  description: z.string(),
});

const labOrderDraftBaseSchema = z.object({
  mode: z.enum(['existing_test', 'custom_test']),
  notes: z.string(),
  priority: z.boolean(),
  aoeAnswersById: z.record(z.string(), z.string()),
  icdCodes: z.array(icd10CodeSchema).min(1, 'At least one ICD-10 diagnosis code is required'),
});

const existingTestDraftSchema = labOrderDraftBaseSchema.extend({
  mode: z.literal('existing_test'),
  labTestId: z.string().trim().min(1, 'Lab test is required'),
  labTestName: z.string().trim().optional(),
});

const markerSchema: z.ZodType<LabMarkerT> = z.custom<LabMarkerT>();

const customTestDraftSchema = labOrderDraftBaseSchema
  .extend({
    mode: z.literal('custom_test'),
    name: z.string().trim().min(1, 'Test name is required'),
    description: z.string().trim().min(1, 'Test description is required'),
    method: labTestCollectionMethodSchema,
    selectedMarkers: z.array(markerSchema).min(1, 'Select at least one marker'),
  })
  .superRefine((draft, ctx) => {
    const result = validateSameLabMarkers(draft.selectedMarkers);
    if (result.ok) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['selectedMarkers'],
      message: 'All selected markers must be from the same laboratory.',
    });
  });

export const labOrderDraftSchema: z.ZodType<LabOrderDraft> = z.discriminatedUnion('mode', [
  existingTestDraftSchema,
  customTestDraftSchema,
]);

export function validateDraft(input: unknown) {
  return labOrderDraftSchema.safeParse(input);
}
